// packages/web/src/api/recharge.ts
/**
 * Recharge API client — order creation + status polling.
 *
 * 19BN Phase D D2: consolidates the two raw fetches that lived in
 * recharge-flow.tsx (create-order + poll order status). All calls go through
 * credentials:'include' so the session cookie travels, and HTTP errors throw
 * IngestApiError (instead of the prior bare `throw new Error(json.error)` /
 * silent poll catch).
 *
 * 401 contract differs by route:
 *   - createOrder (write): 401 throws IngestApiError — the caller surfaces it.
 *     A 401 on a write is a real error, not "no session".
 *   - getOrder (read in poll loop): 401 returns null — the poll loop treats it
 *     as "session expired, stop polling" rather than crashing the interval.
 *
 * Spec: docs/architecture/account-system-design.md §2.3, §9.4
 */

import { INGEST_URL, IngestApiError, accountHeaders } from '../lib/api-client.js';
import type { IngestErrorResponse } from '../lib/api-client.js';

export interface CreateOrderResponse {
  orderId: string;
  package: string;
  amountFen: number;
  credits: number;
  channel: string;
  status: 'pending';
  payUrl: null;
  qrCodeUrl: null;
}

export interface OrderStatusResponse {
  id: string;
  package: string;
  amount_cny: number;
  credits: number;
  channel: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paid_at: number | null;
}

async function postRecharge<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'POST',
    headers: accountHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }) as IngestErrorResponse);
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

async function getRecharge<T>(path: string): Promise<T | null> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
  });
  // 401 = session expired. The poll loop treats this as "stop polling" rather
  // than a hard error (the user will be prompted to log in elsewhere).
  if (res.status === 401) return null;
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }) as IngestErrorResponse);
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

export const rechargeApi = {
  /** Create a pending recharge order. Throws IngestApiError on failure (incl. 401). */
  createOrder: (pkg: string, channel: string) =>
    postRecharge<CreateOrderResponse>('/recharge/create-order', { package: pkg, channel }),
  /** Poll an order's status. Returns null on 401 (session expired → stop polling). */
  getOrder: (orderId: string) =>
    getRecharge<OrderStatusResponse>(`/recharge/order/${orderId}`),
};
